export function deepCopy<T>(obj: T) {
  return JSON.parse(JSON.stringify(obj)) as T;
}

export function getObjectValueByPath<
  TValue,
  TData = unknown,
>(
  data: TData,
  path: string,
): TValue | undefined {
  const result: TValue = path
    .split(/[.[\]]/)
    .filter(Boolean)
    .reduce(
      (value, key) => ((value as Record<string, unknown>)?.[key]),
      data as unknown,
    ) as TValue;

  return result;
}

export function fromEntries<T>(arr: Array<[string, T]>): { [key: string]: T } {
  return Object.assign({}, ...Array.from(arr, ([k, v]) => ({ [k]: v })));
}

function noopSelector<T, V>(el: T): V {
  return (el as unknown) as V;
}

export function filterByKeys<T, V>(obj: Record<string, T>, keys: string[], valueSelector: (el: T) => V = noopSelector) {
  const filteredKeys = Object.keys(obj).filter(key => keys.includes(key));
  const filtered: Record<string, V> = {};

  const mapped = filteredKeys.reduce((acc, current) => {
    acc[current] = valueSelector(obj[current]);
    return acc;
  }, filtered);

  return mapped;
}

type Invert<M extends Record<keyof M, PropertyKey>> = { [K in keyof M as M[K]]: K };

export function createTwoWayMappers<M extends Record<keyof M, PropertyKey>>(map: M) {
  const invertedMap = (Object.entries(map) as Array<[PropertyKey, PropertyKey]>).reduce(
    (inverted, [key, value]) => ({ ...inverted, [value]: key }),
    {},
  ) as Invert<M>;
  const leftToRight = <K extends keyof M>(from: K) => map[from];
  const rightToLeft = <K extends keyof Invert<M>>(to: K) => invertedMap[to];

  return [leftToRight, rightToLeft] as const;
}
